import dayjs, { type Dayjs, extend, locale, type OpUnitType } from 'dayjs';
import ptBR from 'dayjs/locale/pt-br';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
// #region Configuração dayjs
extend(duration);
extend(timezone);
extend(utc);
extend(relativeTime);
extend(customParseFormat);

locale(ptBR);
// #endregion Configuração dayjs
// ----------------------------------------------------------------------

// #region Tipos e constantes
export type DatePickerFormat =
  | Dayjs
  | Date
  | string
  | number
  | null
  | undefined;

const invalidTimeValue = 'Invalid time value';

/**
 * Docs: https://day.js.org/docs/en/display/format
 */
export const formatStr = {
  dateTime: 'DD MMM YYYY h:mm a', // 17 Apr 2022 12:00 am
  date: 'DD MMM YYYY', // 17 Apr 2022
  time: 'h:mm a', // 12:00 am
  time24: 'HH:mm', // 00:00
  split: {
    dateTime: 'DD/MM/YYYY h:mm a', // 17/04/2022 12:00 am
    date: 'DD/MM/YYYY', // 17/04/2022
  },
  paramCase: {
    dateTime: 'DD-MM-YYYY h:mm a', // 17-04-2022 12:00 am
    date: 'DD-MM-YYYY', // 17-04-2022
  },
};
// #endregion Tipos e constantes

// #region Formatações básicas
export const fDateToYear = (date: DatePickerFormat) => {
  return dayjs(date).format('YYYY');
};

/**
 * @param date string: ('2024-12-17T00:00:00') | number | dayjs.Dayjs | Date | null | undefined
 * @returns 23/12/2024
 */
export function fDateBR(date: DatePickerFormat) {
  return dayjs(date).format('DD/MM/YYYY');
}

/**
 * @param date string: ('2024-12-17T00:00:00') | number | dayjs.Dayjs | Date | null | undefined
 * @returns 23-12-2025
 */
export function fDateBR2(date: DatePickerFormat) {
  return dayjs(date).format('DD-MM-YYYY');
}

/**
 * @param date string: ('2024-12-17') | ('2024/12/17') | ('23-12-2025') | ('23/12/2025') | number | dayjs.Dayjs | Date | null | undefined
 * @returns Date | undefined
 */
export function fDateBR3(date: DatePickerFormat) {
  if (!date) return undefined;

  if (dayjs(date, 'YYYY-MM-DD', true).isValid())
    return dayjs(date, 'YYYY-MM-DD', true).toDate();

  if (dayjs(date, 'YYYY/MM/DD', true).isValid())
    return dayjs(date, 'YYYY/MM/DD', true).toDate();

  if (dayjs(date, 'DD-MM-YYYY', true).isValid())
    return dayjs(date, 'DD-MM-YYYY', true).toDate();

  if (dayjs(date, 'DD/MM/YYYY', true).isValid())
    return dayjs(date, 'DD/MM/YYYY', true).toDate();

  if (dayjs(date).isValid()) return dayjs(date).toDate();

  return undefined;
}

/**
 * @param date string: ('2024-12-17T12:00:00') | number | dayjs.Dayjs | Date | null | undefined
 * @returns 17/12/2024 12:00
 */
export function fDateBRWithHour(date: DatePickerFormat) {
  return dayjs(date).format('DD/MM/YYYY HH:mm');
}

/**
 * Formata data/hora em UTC no padrão BR.
 * Útil quando o backend retorna string ISO com 'Z' e você quer exibir a hora "crua" do UTC.
 *
 * @param date string: ('2024-12-17T12:00:00.000Z') | number | dayjs.Dayjs | Date | null | undefined
 * @returns 17/12/2024 12:00
 */
export function fDateBRWithHourUTC(date: DatePickerFormat) {
  return dayjs(date).utc().format('DD/MM/YYYY HH:mm');
}

/**
 * @param date string: ('2024-12-17T00:00:00') | number | dayjs.Dayjs | Date | null | undefined
 * @param hour string: ('1900-01-01T12:00:00') | number | dayjs.Dayjs | Date | null | undefined
 * @returns 17/12/2024 12:00
 */

export const fCombineDateBRWithHour = (date: string, time: string) => {
  const baseDate = dayjs(date);
  const timeDate = dayjs(time);

  return baseDate
    .hour(timeDate.hour())
    .minute(timeDate.minute())
    .format('DD/MM/YYYY HH:mm');
};

/**
 * @param date string (23/12/2024)
 * @returns dayjs.Dayjs
 */
export function parseDateToBRTimezone(dateStr: string) {
  const [day, month, year] = dateStr.split('/');

  const isoString = `${year}-${month}-${day}`;

  return dayjs(isoString).startOf('day');
}

/**
 * @param date string | number | dayjs.Dayjs | Date | null | undefined
 * @returns 12-23-2024
 */
export function fDateUS(date: DatePickerFormat) {
  return dayjs(date).format('MM-DD-YYYY');
}

/**
 * @param date string | number | dayjs.Dayjs | Date | null | undefined
 * @returns 2024-12-23
 */
export function fDateUSReverse(date: DatePickerFormat) {
  return dayjs(date).format('YYYY-MM-DD');
}

export function fDateUSReverseString(date: DatePickerFormat) {
  const parsedDate = dayjs(date, 'DD/MM/YYYY', true);

  if (!parsedDate.isValid()) {
    throw new Error(
      'Data inválida. Certifique-se de que está no formato DD/MM/YYYY.'
    );
  }

  return parsedDate.format('YYYY-MM-DD');
}

/**
 * @param date string | number | dayjs.Dayjs | Date | null | undefined
 * @returns 2024-12-09T03:00:00.000Z
 */
export function fDateUSReverseTimezone(date: DatePickerFormat) {
  const parsedDate = dayjs(date, 'DD/MM/YYYY');

  return parsedDate.isValid()
    ? fDateUSReverse(parsedDate) + 'T03:00:00.000Z'
    : invalidTimeValue;
}

/**
 * Recebe uma data e retorna o número do mês
 * @param date Date - Ex.: 'Sat Mar 29 2025 00:00:00 GMT-0300'
 * @returns number Ex.: 3
 */
export function getNumberMonth(date: Date) {
  return dayjs(date).month() + 1;
}

/**
 * Recebe um número de mês e retorna o nome do mês
 * @param number number - Ex.: 3
 * @returns string Ex.: 'Março'
 */
export function getMonthByNumber(number: number) {
  return dayjs()
    .month(number - 1)
    .format('MMMM');
}

/**
 * Recebe uma data e retorna uma string no formato 'YYYY-MM-DDTHH:mm:ss'
 * @param date string | number | dayjs.Dayjs | Date | null | undefined
 * @returns string Ex.: '2025-03-25T09:36:00'
 */
export function fToIsoTimeString(date: Date): string {
  try {
    return dayjs(date).format('YYYY-MM-DDTHH:mm:ss');
  } catch {
    return invalidTimeValue;
  }
}

/**
 * Recebe uma data e atualiza para a hora atual
 * @param date Date - Ex.: 'Sat Mar 29 2025 00:00:00 GMT-0300'
 * @returns Date com a hora atual
 */
export function fSetCurrentTime(date: DatePickerFormat): Date {
  const currentTime = new Date();

  return dayjs(date)
    .hour(currentTime.getHours())
    .minute(currentTime.getMinutes())
    .second(currentTime.getSeconds())
    .toDate();
}
// #endregion Formatações básicas
// ----------------------------------------------------------------------

// #region Exibição e validação
/** output: 17 Apr 2022 12:00 am
 */
export function fDateTime(date: DatePickerFormat, format?: string) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid
    ? dayjs(date).format(format ?? formatStr.dateTime)
    : invalidTimeValue;
}

// ----------------------------------------------------------------------

/** output: 17 Apr 2022
 */
export function fDate(date: DatePickerFormat, format?: string) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid
    ? dayjs(date).format(format ?? formatStr.date)
    : invalidTimeValue;
}

// ----------------------------------------------------------------------

/** output: 12:00 am
 */
export function fTime(date: DatePickerFormat, format?: string) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid
    ? dayjs(date).format(format ?? formatStr.time)
    : invalidTimeValue;
}

// ----------------------------------------------------------------------

/** output: 12:00 am
 */
export function fTime24(date: DatePickerFormat, format?: string) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid
    ? dayjs(date).format(format ?? formatStr.time24)
    : invalidTimeValue;
}

/* output: 17:00 */
export function fTime24WithoutSufix(date: DatePickerFormat) {
  return dayjs(date).format('HH:mm');
}

/**
 * Recebe uma data e retorna se é válida
 * @param date Date - Ex.: 'Sat Mar 29 2025 00:00:00 GMT-0300'
 * @returns boolean Ex.: true
 */
export function isValidDate(date: DatePickerFormat) {
  return dayjs(date).isValid();
}
// ----------------------------------------------------------------------

/** output: 1713250100
 */
// eslint-disable-next-line sonarjs/function-return-type
export function fTimestamp(date: DatePickerFormat) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid ? dayjs(date).valueOf() : invalidTimeValue;
}

// ----------------------------------------------------------------------

/** output: a few seconds, 2 years
 */
export function fToNow(date: DatePickerFormat) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid ? dayjs(date).toNow(true) : invalidTimeValue;
}
// #endregion Exibição e validação

// #region Comparações de datas

export function fIsBefore(
  startDate: DatePickerFormat,
  endDate: DatePickerFormat
) {
  if (!startDate || !endDate) {
    return false;
  }

  const isValid = dayjs(startDate).isValid() && dayjs(endDate).isValid();

  if (!isValid) {
    return false;
  }

  return dayjs(startDate).isBefore(endDate);
}

/** output: boolean
 */
export function fIsBetween(
  inputDate: DatePickerFormat,
  startDate: DatePickerFormat,
  endDate: DatePickerFormat
) {
  if (!inputDate || !startDate || !endDate) {
    return false;
  }

  const formattedInputDate = fTimestamp(inputDate);
  const formattedStartDate = fTimestamp(startDate);
  const formattedEndDate = fTimestamp(endDate);

  if (formattedInputDate && formattedStartDate && formattedEndDate) {
    return (
      formattedInputDate >= formattedStartDate &&
      formattedInputDate <= formattedEndDate
    );
  }

  return false;
}

// ----------------------------------------------------------------------

/** output: boolean
 */
export function fIsAfter(
  startDate: DatePickerFormat,
  endDate: DatePickerFormat
) {
  return dayjs(startDate).isAfter(endDate);
}

// ----------------------------------------------------------------------

/** output: boolean
 */
export function fIsSame(
  startDate: DatePickerFormat,
  endDate: DatePickerFormat,
  units?: OpUnitType
) {
  if (!startDate || !endDate) {
    return false;
  }

  const isValid = dayjs(startDate).isValid() && dayjs(endDate).isValid();

  if (!isValid) {
    return false;
  }

  return dayjs(startDate).isSame(endDate, units ?? 'year');
}

// #endregion Comparações de datas

// #region Intervalos e rótulos
/**
 * Recebe uma data em 'DD/MM/YYYY'
 * Retorna uma string 'YYYY-MM-DDTHH:mm:ss'
 * Ex.: '06/12/2024' -> '2024-12-06T00:00:00'
 */
export function fToIsoDateString(dateStr: string): string {
  const [day, month, year] = dateStr.split('/');

  const isoDateTime = `${year}-${month}-${day}T00:00:00`;

  return dayjs(isoDateTime).format('YYYY-MM-DDTHH:mm:ss');
}
// ----------------------------------------------------------------------

/** output:
 * Same day: 26 Apr 2024
 * Same month: 25 - 26 Apr 2024
 * Same month: 25 - 26 Apr 2024
 * Same year: 25 Apr - 26 May 2024
 */
export function fDateRangeShortLabel(
  startDate: DatePickerFormat,
  endDate: DatePickerFormat,
  initial?: boolean
) {
  const isValid = dayjs(startDate).isValid() && dayjs(endDate).isValid();

  const isAfter = fIsAfter(startDate, endDate);

  if (!isValid || isAfter) {
    return invalidTimeValue;
  }

  let label = `${fDate(startDate)} - ${fDate(endDate)}`;

  if (initial) {
    return label;
  }

  const isSameYear = fIsSame(startDate, endDate, 'year');
  const isSameMonth = fIsSame(startDate, endDate, 'month');
  const isSameDay = fIsSame(startDate, endDate, 'day');

  if (isSameYear && !isSameMonth) {
    label = `${fDate(startDate, 'DD MMM')} - ${fDate(endDate)}`;
  } else if (isSameYear && isSameMonth && !isSameDay) {
    label = `${fDate(startDate, 'DD')} - ${fDate(endDate)}`;
  } else if (isSameYear && isSameMonth && isSameDay) {
    label = `${fDate(endDate)}`;
  }

  return label;
}
// #endregion Intervalos e rótulos
// ----------------------------------------------------------------------

// #region Durações e cálculos relativos
export type DurationProps = {
  years?: number;
  months?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
  milliseconds?: number;
};

/** output: '2024-05-28T05:55:31+00:00'
 */
export function fAdd({
  years = 0,
  months = 0,
  days = 0,
  hours = 0,
  minutes = 0,
  seconds = 0,
  milliseconds = 0,
}: DurationProps) {
  return dayjs()
    .add(
      dayjs.duration({
        years,
        months,
        days,
        hours,
        minutes,
        seconds,
        milliseconds,
      })
    )
    .format();
}

/** output: '2024-05-28T05:55:31+00:00'
 */
export function fSub({
  years = 0,
  months = 0,
  days = 0,
  hours = 0,
  minutes = 0,
  seconds = 0,
  milliseconds = 0,
}: DurationProps) {
  return dayjs()
    .subtract(
      dayjs.duration({
        years,
        months,
        days,
        hours,
        minutes,
        seconds,
        milliseconds,
      })
    )
    .format();
}

/**
 * Recebe uma data e adiciona um número de anos, meses e dias
 * @param date DatePickerFormat - Ex.: '2024-05-28T05:55:31+00:00'
 * @param years number - Ex.: 1
 * @param months number - Ex.: 1
 * @param days number - Ex.: 1
 * @returns string - Ex.: '29/05/2025'
 */
export function fAddIntoDate(
  date: DatePickerFormat,
  years: number,
  months: number,
  days: number
) {
  return dayjs(date)
    .add(years, 'year')
    .add(months, 'month')
    .add(days, 'day')
    .format('DD/MM/YYYY');
}
// #endregion Durações e cálculos relativos

// #region Nomes de meses
// input: 11 output: 'November'
export const fMonthName = (monthNumber: number) => {
  return dayjs()
    .month(monthNumber - 1)
    .format('MMMM');
};

// input: 11 output: 'Nov'
export const fShortMonthName = (monthNumber: number) => {
  return dayjs()
    .month(monthNumber - 1)
    .format('MMM');
};
// #endregion Nomes de meses

// #region Conversões de tempo e utilidades

/**
 * Converte minutos para uma string formatada de horas e minutos
 * @param minutosTotais - Tempo em minutos
 * @returns String no formato "X hora(s) e Y minuto(s)"
 */
export function fFormatarTempo(minutosTotais: number): string {
  const horas = Math.floor(minutosTotais / 60);
  const minutos = minutosTotais % 60;

  const partes: string[] = [];

  if (horas > 0) {
    partes.push(`${horas} ${horas === 1 ? 'hora' : 'horas'}`);
  }

  if (minutos > 0) {
    partes.push(`${minutos} ${minutos === 1 ? 'minuto' : 'minutos'}`);
  }

  // Caso seja 0 minutos e 0 horas
  if (partes.length === 0) {
    return '0 minutos';
  }

  return partes.join(' e ');
}
/**
 * Calcula a diferença em minutos entre duas datas
 * @param {DatePickerFormat} dataInicial - Data inicial para comparação (formato: 2025-06-25T14:47:24.304Z)
 * @param {DatePickerFormat} dataFinal - Data final para comparação (formato: 2025-06-25T14:47:24.304Z)
 * @returns {number} Diferença em minutos entre as datas
 * @example
 * // Retorna a diferença em minutos entre duas datas
 * const minutos = fDiferencaEmMinutos('2025-06-25T14:47:24.304Z', '2025-06-25T15:47:24.304Z')
 * console.log(minutos) // 60
 */
export function fDiferencaEmMinutos(
  dataInicial: DatePickerFormat,
  dataFinal: DatePickerFormat
): number {
  const inicio = dayjs(dataInicial);
  const fim = dayjs(dataFinal);

  return fim.diff(inicio, 'minute');
}

/**
 * Retorna a data atual ou a data atual com hora atual
 * @param {boolean} usarHoraAtual - Se true, retorna a data atual com hora atual
 * @returns {Date} Data atual ou data atual com hora atual
 */
export function today({ usarHoraAtual = false }: { usarHoraAtual?: boolean }) {
  const dia = dayjs();

  return usarHoraAtual ? dia.toDate() : dia.startOf('day').toDate();
}
// #endregion Conversões de tempo e utilidades

// #region Formatações Brasília (Timezone America/Sao_Paulo)
const TIMEZONE_BRASILIA = 'America/Sao_Paulo';

/**
 * Normaliza a data para lidar com o problema de fuso horário.
 * Se o banco salva o horário nominal de Brasília como UTC, precisamos "desfazer" isso.
 * Caso contrário, o horário e o tempo relativo (distância) ficam errados.
 */
export function fNormalizedDate(date: DatePickerFormat): Date | undefined {
  if (!date || !dayjs(date).isValid()) return undefined;

  // Pegamos os valores nominais (UTC) e os tratamos como sendo de Brasília.
  const dateStr = dayjs(date).utc().format('YYYY-MM-DD HH:mm:ss');

  return dayjs.tz(dateStr, TIMEZONE_BRASILIA).toDate();
}

/**
 * Formata a data para exibição no padrão de Brasília.
 * Ex: 17 de mar. de 2026
 */
export function fDateBrasilia(date: DatePickerFormat) {
  const normalized = fNormalizedDate(date);

  return normalized
    ? dayjs(normalized).tz(TIMEZONE_BRASILIA).format('DD [de] MMM. [de] YYYY')
    : '';
}

/**
 * Formata a hora para exibição no padrão de Brasília.
 * Ex: 08:34
 */
export function fTimeBrasilia(date: DatePickerFormat) {
  const normalized = fNormalizedDate(date);

  return normalized
    ? dayjs(normalized).tz(TIMEZONE_BRASILIA).format('HH:mm')
    : '';
}

/**
 * Calcula a distância relativa no tempo (ex: "há 2 horas").
 */
export function fDistanceBrasilia(date: DatePickerFormat) {
  const normalized = fNormalizedDate(date);

  return normalized ? dayjs(normalized).tz(TIMEZONE_BRASILIA).fromNow() : '';
}
// #endregion
